import React, { useRef, useCallback, useEffect } from 'react';

export type InfiniteScrollObserverOptions = {
  root?: HTMLElement | null;
  rootMargin?: string;
  threshold?: number;
};

export type InfiniteScrollObserverProps = {
  onScrollIn?: () => void;
  onScrollOut?: () => void;
  options?: InfiniteScrollObserverOptions;
  height?: number;
  style?: React.CSSProperties;
};

export const InfiniteScrollObserver: React.FC<InfiniteScrollObserverProps> = ({
  onScrollIn = () => null,
  onScrollOut = () => null,
  options = {},
  height = 1,
  style = {},
}) => {
  const observerRef = useRef(null);
  const { root = null, rootMargin = '0px', threshold = 0 } = options;

  const handleObserver: IntersectionObserverCallback = useCallback(
    (entries) => {
      const target = entries[0];
      if (target.isIntersecting) {
        onScrollIn();
      } else {
        onScrollOut();
      }
    },
    [onScrollIn, onScrollOut],
  );

  useEffect(() => {
    const option = {
      root,
      rootMargin,
      threshold,
    };
    const observer = new IntersectionObserver(handleObserver, option);
    if (observerRef.current) {
      observer.observe(observerRef.current);
      return () => observer.disconnect();
    }
    return () => {};
  }, [handleObserver, root, rootMargin, threshold]);

  return (
    <div
      style={{ height, width: '100%', display: 'block', ...style }}
      ref={observerRef}
    />
  );
};
